import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Meta from '../components/Meta';
import RedirectCard from '../components/RedirectCard';
import { useEvent } from '../data/store';
import { marketplaceBySlug, marketplaceName } from '../data/marketplaces';
import { fmtDateDow, fmtTime } from '../lib/format';
import { readHandoff, type Handoff } from '../lib/handoff';
import { useLocale, useT } from '../i18n';
import { isAllIn } from '../../shared/providers.mjs';
import { REDIRECT_MS, outPlan, visitTags } from '../../shared/out.mjs';

/** What the /out/ shell's inline script leaves behind when it owns the hand-off (shared/out.mjs). */
type ShellOut = { cont: string; tracked: string | null; started: boolean; wait: number };

type Q = {
  u: string;
  eventId: string;
  /** the Continue links' URL (never with ref) */
  cont: string | null;
  /** the timer's and a plain Continue click's URL (with ref); null without a ref */
  tracked: string | null;
  /** the shell's inline script runs the timer and the Continue clicks */
  owned: boolean;
  /** the page goes on to the partner by itself */
  auto: boolean;
  /** the progress bar's duration, constant once set (a re-render never restarts the bar) */
  wait: number;
  handoff: Handoff | null;
};

const navigationType = () => {
  try {
    return (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type ?? 'navigate';
  } catch {
    return 'navigate';
  }
};

/** The quantity a HelloTickets link carries: its URL ends in /<qty> (src/data/marketplaces.ts goHref). */
const qtyInUrl = (u: string | undefined) => {
  try {
    const n = Number(new URL(u ?? '').pathname.match(/\/(\d{1,2})$/)?.[1]);
    return n >= 1 && n <= 20 ? n : null;
  } catch {
    return null;
  }
};

/**
 * /out/:market — the redirect interstitial. Shows the RedirectCard for ~2 s, then continues to the
 * edge router at /go/:market, which keeps every job it already had: host allowlist (no open
 * redirect), partner tracking params, the click log, and the 302. The Continue button goes there
 * immediately.
 *
 * Query: u (destination), event (our id), p (placement). The page the click came from
 * (document.referrer, same-origin only) travels along as `ref`: the router tracks a click only with
 * it (shared/go-gate.mjs). Opened without one — a typed or shared link, or a crawler opening /out/
 * URLs directly (203 of 570 tracked clicks, 6–13 Oct 2026) — the page does not redirect by itself,
 * and Continue still reaches the partner, untracked. Nor after Back/Forward or a reload: a visitor
 * who pressed Back must not be bounced forward again.
 *
 * `ref` never appears in an href: the tracked URL is only navigated to by the timer or a plain click
 * on Continue, so nothing that reads the page's links can replay it.
 *
 * WHO RUNS THE TIMER (2026-10-19). The prerendered shell's inline script (shared/out.mjs, the same
 * outPlan rules) sets the Continue links and starts the timer as the HTML arrives, counted from the
 * click; it leaves window.__out, and this page then attaches no timer and no click listener of its
 * own (one tap must navigate once) and keeps the bar's duration. This page's own timer is the
 * fallback for when no shell script ran (the SPA fallback: a dev server, an unknown partner). With
 * JavaScript off, the edge writes the untracked Continue URL into the HTML (functions/_middleware.ts).
 *
 * The card repeats what the board showed: the hand-off record the click left (src/lib/handoff.ts)
 * when it names this event and partner and is under two minutes old, otherwise the event's own
 * offer from this partner, never a number from the URL: a crafted link could otherwise make the
 * card announce any price. (Links still carry ?price=; it is ignored.)
 *
 * The query and the record are read after mount: /out/<market> is prerendered as a shell, and the
 * shell must match the first client render exactly or React drops the whole tree and re-renders.
 */
const Go = () => {
  const { market = '' } = useParams();
  const mk = marketplaceBySlug(market);
  const name = marketplaceName(market);
  const t = useT();
  const locale = useLocale();
  const [q, setQ] = useState<Q | null>(null);

  useEffect(() => {
    // The click's coarse tags (kind of site that sent this browser, returning or not) ride on the
    // tracked URL only, like ref (shared/out.mjs visitTags; functions/go/[market].ts counts them).
    let tags: ReturnType<typeof visitTags> = { s: null, rv: null };
    try {
      tags = visitTags(window.localStorage, Date.now());
    } catch {
      /* storage blocked: unknown */
    }
    const plan = outPlan({ market, search: window.location.search, referrer: document.referrer, origin: window.location.origin, tags });
    const pre = (window as unknown as { __out?: ShellOut }).__out;
    const shell = pre && pre.cont === plan.cont ? pre : null;
    const nav = navigationType();
    const auto = shell ? shell.started : !!plan.tracked && nav !== 'back_forward' && nav !== 'reload';
    setQ({
      u: plan.u,
      eventId: plan.event,
      cont: plan.cont,
      tracked: plan.tracked,
      owned: !!shell,
      auto,
      wait: shell?.started ? shell.wait : REDIRECT_MS,
      handoff: plan.event ? readHandoff(plan.event, market) : null,
    });
  }, [market]);

  // The fallback: only when the shell's script did not run.
  useEffect(() => {
    if (!q || q.owned || !q.u || !q.cont || !q.tracked) return;
    const cont = q.cont;
    const tracked = q.tracked;
    const timer = q.auto ? window.setTimeout(() => window.location.replace(tracked), REDIRECT_MS) : 0;
    // A plain click on Continue (either link on the card) goes to the tracked URL itself and
    // stops the timer: the timer must not send a second request to /go while the partner's page
    // is still loading — that logged one click twice (7 of 33 real clicks, 2-6 Oct 2026). A
    // click that opens a new tab or window keeps the link's own, untracked, href.
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a');
      if (!a || a.getAttribute('href') !== cont) return;
      window.clearTimeout(timer);
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      window.location.assign(tracked);
    };
    document.addEventListener('click', onClick, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', onClick, true);
    };
  }, [q]);

  const h = q?.handoff ?? null;
  // The nightly row is needed only without a hand-off record.
  const { event, loading } = useEvent(q && !h && q.eventId ? q.eventId : undefined);

  if (q && (!q.u || !market)) {
    return (
      <div className="shell py-24 text-center">
        <Meta title="Link incomplete | TixInfo" noindex />
        <p className="text-lg font-medium">{t('This link is missing its destination.')}</p>
        <Link to="/" className="mt-4 inline-block text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
          {t('Back to Tixinfo')}
        </Link>
      </div>
    );
  }

  const offer = !h ? (event?.offers?.find((o) => o.source === name) ?? null) : null;
  const show = h
    ? { name: h.artist ?? h.name, date: h.date, time: h.time, venue: h.venue, city: h.city }
    : event
      ? { name: event.artist ?? event.name, date: event.date, time: event.time ?? null, venue: event.venue, city: event.city }
      : null;
  // HelloTickets opens with the quantity its link carries; that, not the record, is what its page will show.
  const presetQty = mk?.quantity === 'preset' ? qtyInUrl(q?.u) : null;

  return (
    <div className="shell flex min-h-[70vh] items-center justify-center py-16">
      <Meta title={`Taking you to ${name} | TixInfo`} noindex />
      <RedirectCard
        marketplace={name}
        event={
          show
            ? {
                name: show.name,
                when: `${fmtDateDow(show.date, locale)}${show.time ? ` · ${fmtTime(show.time, locale)}` : ''}`,
                venue: `${show.venue}, ${show.city}`,
              }
            : undefined
        }
        price={h ? h.price : (offer?.from ?? null)}
        allIn={h ? (h.allIn ?? undefined) : offer ? isAllIn(offer) : undefined}
        suspect={h ? h.suspect : !!offer?.suspect}
        qty={h?.qty ?? presetQty}
        quantity={mk?.quantity}
        presetQty={presetQty}
        // the shell, and a page still looking for its event, hold the block's place
        pending={!q || (!h && !event && loading)}
        shell={!q}
        // The prerendered shell (q not read yet) shows the redirecting card, as every real click sees it.
        auto={!q || q.auto}
        autoMs={q?.wait ?? REDIRECT_MS}
        continueHref={q?.cont ?? '#'}
      />
    </div>
  );
};

export default Go;
